import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { almacen, type Supply, type SupplyPackage } from './api';
import { Boton, Fallo, Modal } from './componentes';
import { paraEnviar, problemaDeEmpaques, totalPorEmpaques, type EmpaqueTecleado } from './empaquesDelInsumo';
import { cantidad } from './formato';

/**
 * Los empaques en que llega un insumo: el rollo de 50 m y el de 100 m, la caja
 * de 100 y la de 500 (migración 276, docs/15 §2.52).
 *
 * La ferretería compra el mismo cable en dos rollos y el mismo tornillo en dos
 * cajas; con una sola unidad de compra en la ficha, el otro empaque se recibía
 * multiplicando de cabeza. Aquí se dice cuánto trae cada uno y, si lo tiene,
 * su código: en la recepción se elige —o se escanea— y entra convertido, y en
 * el conteo se cuentan rollos cerrados más los metros del abierto.
 */
export function Empaques({ insumo }: { insumo: Supply }) {
  const clientes = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [lista, setLista] = useState<EmpaqueTecleado[]>([]);

  const guardar = useMutation({
    mutationFn: () => almacen.setPackages(insumo.id, paraEnviar(lista)),
    onSuccess: () => {
      setAbierto(false);
      void clientes.invalidateQueries({ queryKey: ['insumos'] });
    },
  });

  const abrir = () => {
    setLista(
      (insumo.packages ?? []).map((empaque) => ({
        id: empaque.id,
        nombre: empaque.name,
        // El número tal cual, con coma: `cantidad` agrupa miles y no se
        // vuelve a leer igual.
        trae: String(Number.parseFloat(empaque.units)).replace('.', ','),
        codigo: empaque.barcode ?? '',
      })),
    );
    guardar.reset();
    setAbierto(true);
  };

  const cuantos = insumo.packages?.length ?? 0;

  if (!abierto) {
    return (
      <button type="button" className="accion-fila" onClick={abrir}>
        {cuantos === 0 ? 'Empaques' : `Empaques (${cuantos})`}
      </button>
    );
  }

  const problema = problemaDeEmpaques(lista);
  const cambiar = (indice: number, campo: keyof EmpaqueTecleado, valor: string) =>
    setLista((antes) => antes.map((uno, i) => (i === indice ? { ...uno, [campo]: valor } : uno)));

  return (
    <Modal titulo={`Empaques de ${insumo.name}`} onCerrar={() => setAbierto(false)}>
      <div className="formulario">
        <p className="nota">
          En qué llega, además de {insumo.unit}: cuántos {insumo.unit} trae cada empaque y, si lo
          tiene, el código de la caja. Al recibir se elige —o se escanea— y entra convertido.
        </p>

        {lista.length === 0 ? (
          <p className="apagado">Todavía sin empaques: llega y se cuenta en {insumo.unit}.</p>
        ) : (
          lista.map((empaque, indice) => (
            <div key={empaque.id ?? `nuevo-${indice}`} className="formulario__fila">
              <div className="campo">
                <label htmlFor={`empaque-nombre-${indice}`}>Nombre</label>
                <input
                  id={`empaque-nombre-${indice}`}
                  placeholder="Rollo 100 m"
                  value={empaque.nombre}
                  maxLength={40}
                  onChange={(evento) => cambiar(indice, 'nombre', evento.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor={`empaque-trae-${indice}`}>Trae ({insumo.unit})</label>
                <input
                  id={`empaque-trae-${indice}`}
                  inputMode="decimal"
                  placeholder="100"
                  value={empaque.trae}
                  onChange={(evento) => cambiar(indice, 'trae', evento.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor={`empaque-codigo-${indice}`}>Código (si tiene)</label>
                <input
                  id={`empaque-codigo-${indice}`}
                  className="mono"
                  value={empaque.codigo}
                  maxLength={64}
                  onChange={(evento) => cambiar(indice, 'codigo', evento.target.value)}
                />
              </div>
              <button
                type="button"
                className="enlace-sutil"
                onClick={() => setLista((antes) => antes.filter((_, i) => i !== indice))}
              >
                Quitar
              </button>
            </div>
          ))
        )}

        <button
          type="button"
          className="enlace-sutil"
          onClick={() => setLista((antes) => [...antes, { nombre: '', trae: '', codigo: '' }])}
        >
          + Otro empaque
        </button>

        {problema !== null && lista.length > 0 ? <p className="negativo">{problema}</p> : null}
        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={problema !== null || guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Contar por empaque: «3 rollos de 100 m cerrados + 47 m del abierto».
 *
 * El cable no se desenrolla para contarlo: se cuentan los rollos cerrados y se
 * mide el abierto. Abrir un rollo no es un movimiento —el stock está en
 * metros (§2.52)—, así que esto solo suma y deja el total en la línea del
 * conteo, en la unidad del insumo.
 */
export function ContarPorEmpaque({
  nombre,
  unidad,
  empaques,
  onTotal,
}: {
  nombre: string;
  unidad: string;
  empaques: readonly SupplyPackage[];
  onTotal: (total: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [cerrados, setCerrados] = useState<Record<string, string>>({});
  const [sueltos, setSueltos] = useState('');

  if (empaques.length === 0) return null;

  if (!abierto) {
    return (
      <button type="button" className="enlace-sutil" onClick={() => setAbierto(true)}>
        por empaque
      </button>
    );
  }

  const total = totalPorEmpaques(empaques, cerrados, sueltos);

  return (
    <Modal titulo={`Contar ${nombre} por empaque`} onCerrar={() => setAbierto(false)}>
      <div className="formulario">
        <p className="nota">
          Los cerrados por empaque, y lo suelto o del abierto en {unidad}. Abrir uno no se registra:
          aquí solo se suma.
        </p>
        {empaques.map((empaque) => (
          <div key={empaque.id} className="campo">
            <label htmlFor={`cerrados-${empaque.id}`}>
              {empaque.name} cerrados ({cantidad(empaque.units)} {unidad} c/u)
            </label>
            <input
              id={`cerrados-${empaque.id}`}
              inputMode="numeric"
              value={cerrados[empaque.id] ?? ''}
              onChange={(evento) =>
                setCerrados((antes) => ({ ...antes, [empaque.id]: evento.target.value }))
              }
            />
          </div>
        ))}
        <div className="campo">
          <label htmlFor="sueltos">Suelto o del abierto ({unidad})</label>
          <input
            id="sueltos"
            inputMode="decimal"
            value={sueltos}
            onChange={(evento) => setSueltos(evento.target.value)}
          />
        </div>
        <p className="mono">
          {total === null ? 'Los cerrados van enteros.' : `Total: ${cantidad(String(total))} ${unidad}`}
        </p>
        <div className="formulario__fila">
          <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
          <Boton
            variante="primario"
            disabled={total === null}
            onClick={() => {
              onTotal(String(total).replace('.', ','));
              setAbierto(false);
            }}
          >
            Poner en el conteo
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
